import {
  DataGetCollectionProps,
  FileUploadInfo,
  getDocuments,
  IAuthorizationService,
  IDataAccessService,
  IDocumentService,
  IEventService,
  IExportService,
  IFileAccessService,
  IFilter,
  ILookupService,
  ObjectTyped,
  SortOrder,
} from '@price-for-profit/micro-services';
import { unparse } from 'papaparse';
import {
  ABG_DIRECTORS,
  ABG_PROJECT_TYPES,
  ABG_QUOTING_APPROVERS,
  ABG_QUOTING_BRANDS,
  ABG_REP_AGENCIES,
  CLIENT_ID,
  DATASTORE_DOCUMENT,
  QUOTE_TABLE,
} from 'shared/constants';
import {
  ABGQuotingApproversLookupItem,
  QuoteBrandsLookup,
  QuoteDirectorsLookup,
  QuoteDocument,
  QuoteDocumentWithoutId,
  QuoteProjectTypesLookup,
  QuoteRepAgenciesLookup,
  QuotesSummaryItem,
  SearchFilters,
  SortableFields,
} from 'shared/types';
import {
  browserDownloadCsv,
  formatDate,
  formatDateTimeEST,
  getSupportEmailTemplate,
  getWeekExportStartEnd,
  supportEmailTemplateProps,
} from 'shared/utility';
import { DownloadFileProps } from '.';

type QuoteDocumentQuery = Pick<
  DataGetCollectionProps<QuoteDocument, any>,
  'collectionFilter' | 'page' | 'pageSize' | 'sortBy' | 'sortDescending'
>;

export interface GetQuotesResponse {
  filteredQuotes: QuotesSummaryItem[];
  totalCount: number;
  totalPages: number | undefined;
  currentPage: number;
}

export interface ExportQuotesResponse {
  filteredQuotes: QuotesSummaryItem[];
}

export interface GetQuotesProps {
  page?: number;
  pageSize?: number;
  search: SearchFilters;
  sortOrder?: SortOrder;
  sortBy?: SortableFields;
  userEmail: string;
  userIsAdmin?: boolean;
  userId?: string;
}

export interface DownloadPdfProps<T> {
  exportId: string;
  fileName?: string;
  orientation?: 'landscape' | 'portrait';
  data: T;
}

export interface IQuoteService {
  createQuote: (quote: QuoteDocumentWithoutId) => Promise<QuoteDocument>;
  updateQuote: (quote: QuoteDocument) => Promise<{ operationId: string }>;
  getQuotes: (props: GetQuotesProps) => Promise<GetQuotesResponse>;
  getQuote: (id: string) => Promise<QuoteDocument>;
  downloadPdf<T>(props: DownloadPdfProps<T>): Promise<void>;
  uploadQuoteFile: (files: FormData) => Promise<FileUploadInfo[]>;
  downloadQuotesSummary: (props: GetQuotesProps) => Promise<boolean>;
  deleteQuoteFile: (fileId: string) => Promise<boolean>;
  downloadQuoteFile: (props: DownloadFileProps) => Promise<void>;
  sendSupportEmail: (props: supportEmailTemplateProps) => Promise<void>;
  getQuoteBrands: () => Promise<string[]>;
  getQuoteDirectors: () => Promise<string[]>;
  getQuoteApprovers: () => Promise<string[]>;
  getQuoteProjectTypes: () => Promise<string[]>;
  getRepAgencies: () => Promise<string[]>;
}

export class QuoteService implements IQuoteService {
  private shared: { type: string; clientId: string } = { type: QUOTE_TABLE, clientId: CLIENT_ID };
  constructor(
    private dataAccessService: IDataAccessService,
    private documentService: IDocumentService,
    private fileAccessService: IFileAccessService,
    private exportService: IExportService,
    private lookupService: ILookupService,
    private eventService: IEventService,
    private authService: IAuthorizationService
  ) {}

  async uploadQuoteFile(files: FormData): Promise<FileUploadInfo[]> {
    return await this.fileAccessService.uploadFile({
      clientId: CLIENT_ID,
      files,
    });
  }

  async deleteQuoteFile(fileId: string): Promise<boolean> {
    await this.fileAccessService.deleteFile({
      clientId: CLIENT_ID,
      fileName: fileId,
    });

    return true;
  }

  async createQuote(quote: QuoteDocumentWithoutId): Promise<QuoteDocument> {
    const { data } = await this.documentService.create<QuoteDocumentWithoutId, QuoteDocument>({
      ...this.shared,
      payload: quote,
    });

    return data;
  }

  async updateQuote(quote: QuoteDocument): Promise<{ operationId: string }> {
    const { metadata } = await this.documentService.update<QuoteDocument>({
      ...this.shared,
      id: quote.id,
      payload: quote,
    });

    return metadata;
  }

  async getQuote(id: string): Promise<QuoteDocument> {
    const { data } = await this.documentService.getById<QuoteDocument>({
      ...this.shared,
      id,
    });
    return data;
  }

  async getQuotesQuery({
    page,
    pageSize,
    search,
    sortOrder,
    sortBy,
    userIsAdmin,
    userId,
  }: GetQuotesProps): Promise<QuoteDocumentQuery> {
    let allowedRepAgencies: string[] = [];

    if (userId) {
      allowedRepAgencies = await this.getUserRepAgencies(userId);
    }

    const hasRepAgency = allowedRepAgencies?.length;
    const collectionFilters: IFilter<QuoteDocument>[] = [
      {
        property: 'dateDeleted',
        operator: 'eq',
        value: '',
      },
    ];

    if (!!search.quoteNumber) {
      collectionFilters.push({
        property: 'quoteNumber',
        operator: 'contains',
        value: search.quoteNumber.toUpperCase(),
      });
    }

    if (!!search.wholesalerCustomerNumber) {
      collectionFilters.push({
        property: 'wholesalerCustomerNumber',
        operator: 'contains',
        value: search.wholesalerCustomerNumber.toUpperCase(),
      });
    }

    if (!!search.jobInfoProjectName) {
      collectionFilters.push({
        property: 'jobInfoProjectName',
        operator: 'contains',
        value: search.jobInfoProjectName.toUpperCase(),
      });
    }

    if (!!search.plumberProjectType) {
      collectionFilters.push({
        property: 'plumberProjectType',
        operator: 'contains',
        value: search.plumberProjectType.toUpperCase(),
      });
    }

    if (!!search.wholesaler) {
      collectionFilters.push({
        property: 'wholesaler',
        operator: 'contains',
        value: search.wholesaler.toUpperCase(),
      });
    }

    if (!!search.plumberContractor) {
      collectionFilters.push({
        property: 'plumberContractor',
        operator: 'contains',
        value: search.plumberContractor.toUpperCase(),
      });
    }

    if (!!search.brand) {
      collectionFilters.push({
        property: 'brand',
        operator: 'contains',
        value: search.brand.toUpperCase(),
      });
    }

    if (!!search.requestedBy) {
      collectionFilters.push({
        property: 'requestedBy',
        operator: 'contains',
        value: search.requestedBy.toUpperCase(),
      });
    }

    if (!!search.status) {
      collectionFilters.push({
        property: 'status',
        operator: 'contains',
        value: search.status.toUpperCase(),
      });
    }

    if (!!search.approvedBy) {
      collectionFilters.push({
        property: 'approvedBy',
        operator: 'contains',
        value: search.approvedBy.toUpperCase(),
      });
    }

    if (!!search.revisionApprovedBy) {
      collectionFilters.push({
        property: 'revisionApprovedBy',
        operator: 'contains',
        value: search.revisionApprovedBy.toUpperCase(),
      });
    }

    if (!!search.lastModifiedBy) {
      collectionFilters.push({
        property: 'lastModifiedBy',
        operator: 'contains',
        value: search.lastModifiedBy.toUpperCase(),
      });
    }

    if (!!search.jobInfoStateName) {
      collectionFilters.push({
        property: 'jobInfoStateName',
        operator: 'contains',
        value: search.jobInfoStateName.toUpperCase(),
      });
    }

    if (!!search.dateSubmitted[0] && !!search.dateSubmitted[1]) {
      collectionFilters.push({
        property: 'dateSubmitted',
        operator: 'gte',
        value: search.dateSubmitted[0],
      });
      collectionFilters.push({
        property: 'dateSubmitted',
        operator: 'lte',
        value: search.dateSubmitted[1],
      });
    }

    if (!!search.dateApproved[0] && !!search.dateApproved[1]) {
      collectionFilters.push({
        property: 'dateApproved',
        operator: 'gte',
        value: search.dateApproved[0],
      });
      collectionFilters.push({
        property: 'dateApproved',
        operator: 'lte',
        value: search.dateApproved[1],
      });
    }

    if (!!search.dateRevised[0] && !!search.dateRevised[1]) {
      collectionFilters.push({
        property: 'dateRevised',
        operator: 'gte',
        value: search.dateRevised[0],
      });
      collectionFilters.push({
        property: 'dateRevised',
        operator: 'lte',
        value: search.dateRevised[1],
      });
    }

    if (userIsAdmin) {
      if (!!search.repAgency) {
        collectionFilters.push({
          property: 'repAgency',
          operator: 'contains',
          value: search.repAgency.toUpperCase(),
        });
      }
    } else if (hasRepAgency) {
      collectionFilters.push({
        property: 'repAgency',
        operator: 'in',
        value: allowedRepAgencies,
      });
    } else {
      collectionFilters.push({
        property: 'repAgency',
        operator: 'contains',
        value: 'NOTANAGENCY',
      });
    }

    const query: QuoteDocumentQuery = {
      collectionFilter:
        collectionFilters.length > 1
          ? {
              logicalOperator: 'and',
              filters: collectionFilters,
            }
          : collectionFilters[0],
      sortBy: sortBy ?? undefined,
      sortDescending: sortOrder === 'desc',
      page: page === undefined ? undefined : page,
      pageSize: pageSize === undefined ? undefined : pageSize,
    };
    return query;
  }

  getFilteredQuotesFromData(data: QuoteDocument[]): QuotesSummaryItem[] {
    return data.map(quote => ({
      id: quote.id,
      quoteNumber: quote.quoteNumber,
      wholesalerCustomerNumber: quote.wholesalerCustomerNumber,
      jobInfoProjectName: quote.jobInfoProjectName,
      jobInfoStateName: quote.jobInfoStateName,
      plumberProjectType: quote.plumberProjectType,
      wholesaler: quote.wholesaler,
      plumberContractor: quote.plumberContractor,
      brand: quote.brand,
      repAgency: quote.repAgency,
      requestedBy: quote.requestedBy,
      quoteTotal: 0,
      dateSubmitted: quote.dateSubmitted,
      dateApproved: quote.dateApproved,
      dateRevised: quote.dateRevised,
      status: quote.status ?? 'Submitted & Received',
      approvedBy: quote.approvedBy,
      revisionApprovedBy: quote.revisionApprovedBy,
      lastModifiedBy: quote.lastModifiedBy,
    }));
  }

  async getQuotes(props: GetQuotesProps): Promise<GetQuotesResponse> {
    try {
      const query = await this.getQuotesQuery(props);

      console.log('here?');
      const { data, metadata } = await this.dataAccessService.getCollection<QuoteDocument>({
        clientId: this.shared.clientId,
        tableId: this.shared.type,
        databaseLabel: DATASTORE_DOCUMENT,
        ...query,
      });
      console.log(data);

      const { totalCount, totalPages, currentPage } = metadata;
      const filteredQuotes: QuotesSummaryItem[] = this.getFilteredQuotesFromData(data);
      return { filteredQuotes, totalCount, totalPages, currentPage };
    } catch (err) {
      throw err;
    }
  }

  async getAllQuotes(props: GetQuotesProps): Promise<ExportQuotesResponse> {
    try {
      const query = await this.getQuotesQuery(props);
      const data = await getDocuments(page =>
        this.dataAccessService.getCollection<QuoteDocument>({
          clientId: this.shared.clientId,
          tableId: this.shared.type,
          databaseLabel: DATASTORE_DOCUMENT,
          ...query,
          page,
        })
      );
      const filteredQuotes: QuotesSummaryItem[] = this.getFilteredQuotesFromData(data);
      return { filteredQuotes };
    } catch (err) {
      throw err;
    }
  }

  private async getUserRepAgencies(userId: string): Promise<string[]> {
    try {
      const user = await this.authService.getClientUserRestricted({ clientId: CLIENT_ID, userId });

      const client = user.clients.find(clients => clients.clientId === CLIENT_ID);

      const repAgenciesAttributeValue = client?.attributes?.repagency;

      return repAgenciesAttributeValue?.split('|').map(v => v.trim().toUpperCase()) || [];
    } catch (err) {
      return [];
    }
  }
  async getQuoteBrands() {
    try {
      const data = await this.lookupService.filteredAndSearch<QuoteBrandsLookup>({
        clientId: CLIENT_ID,
        table: ABG_QUOTING_BRANDS,
        columnFilters: [],
      });
      return data?.map(option => option.brand).sort();
    } catch (err) {
      throw err;
    }
  }

  async getQuoteDirectors() {
    try {
      const data = await this.lookupService.filteredAndSearch<QuoteDirectorsLookup>({
        clientId: CLIENT_ID,
        table: ABG_DIRECTORS,
        columnFilters: [],
      });
      return data?.map(option => option.director).sort();
    } catch (err) {
      throw err;
    }
  }

  async getQuoteApprovers() {
    try {
      const data = await this.lookupService.filteredAndSearch<ABGQuotingApproversLookupItem>({
        clientId: CLIENT_ID,
        table: ABG_QUOTING_APPROVERS,
        columnFilters: [],
      });
      return data?.map(option => option.approver).sort();
    } catch (err) {
      throw err;
    }
  }

  async getQuoteProjectTypes() {
    try {
      const data = await this.lookupService.filteredAndSearch<QuoteProjectTypesLookup>({
        clientId: CLIENT_ID,
        table: ABG_PROJECT_TYPES,
        columnFilters: [],
      });
      return data?.map(option => option.projecttype).sort();
    } catch (err) {
      throw err;
    }
  }

  async getRepAgencies() {
    try {
      const data = await this.lookupService.filteredAndSearch<QuoteRepAgenciesLookup>({
        clientId: CLIENT_ID,
        table: ABG_REP_AGENCIES,
        columnFilters: [],
      });
      return data?.map(option => option.agencyname).sort();
    } catch (err) {
      throw err;
    }
  }

  async downloadQuotesSummary({ search, sortOrder, sortBy, userEmail, userId, userIsAdmin }: GetQuotesProps) {
    const today = new Date();
    let allowedRepAgency = undefined;

    if (userId) {
      allowedRepAgency = await this.getUserRepAgencies(userId);
    }

    if (!userIsAdmin && !allowedRepAgency) {
      throw new Error('Export is not available');
    }

    const { filteredQuotes } = await this.getAllQuotes({
      search,
      sortOrder,
      sortBy,
      userEmail,
      userIsAdmin,
    });
    const [start, end] = getWeekExportStartEnd(today);
    const fileName = `quotes-${formatDate(start)}-${formatDate(end)}.csv`;

    if (!filteredQuotes || (filteredQuotes && !filteredQuotes.length)) {
      return false;
    }

    const fields = [
      'Status',
      'Quote #',
      'Customer',
      'Project Name',
      'Project Type',
      'Brand',
      'Rep Agency',
      'Rep',
      'Date Submitted',
      'Date Revised',
      'Date Approved',
      'Wholesaler',
      'Plumber',
      'Approver',
      'Revision Approver',
      'Last Modified By',
      'jobInfoStateName',
    ];

    const dataToExport = filteredQuotes.map(quote => {
      return {
        status: quote.status,
        quoteNumber: quote.quoteNumber,
        wholesalerCustomerNumber: quote.wholesalerCustomerNumber,
        jobInfoProjectName: quote.jobInfoProjectName,
        plumberProjectType: quote.plumberProjectType,
        brand: quote.brand,
        repAgency: quote.repAgency,
        requestedBy: quote.requestedBy,
        dateSubmitted: quote.dateSubmitted ? formatDateTimeEST(new Date(quote.dateSubmitted)) : '',
        dateRevised: quote.dateRevised ? formatDateTimeEST(new Date(quote.dateRevised)) : '',
        dateApproved: quote.dateApproved ? formatDateTimeEST(new Date(quote.dateApproved)) : '',
        wholesaler: quote.wholesaler,
        plumberContractor: quote.plumberContractor,
        approvedBy: quote.approvedBy,
        revisionApprovedBy: quote.revisionApprovedBy,
        lastModifiedBy: quote.lastModifiedBy,
        jobInfoStateName: quote.jobInfoStateName,
      };
    });

    const csvData = dataToExport.map(record => ObjectTyped.values(record));
    const csv = unparse({ data: csvData, fields });
    browserDownloadCsv(csv, fileName);

    return true;
  }

  async downloadQuoteFile(props: DownloadFileProps): Promise<void> {
    const file = await this.fileAccessService.getFile({
      clientId: CLIENT_ID,
      fileName: props.fileId,
    });

    this.handleBlob(file, props.originalFileName);
  }

  private handleBlob(blob: Blob, fileName: string) {
    const blobUrl = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.parentNode?.removeChild(link);
  }

  async downloadPdf<T>({ exportId, data, fileName = 'export', orientation = 'portrait' }: DownloadPdfProps<T>) {
    const blob = await this.exportService.downloadPdf<T>({
      clientId: CLIENT_ID,
      templateId: exportId,
      payload: { orientation, data },
    });

    const blobUrl = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.parentNode?.removeChild(link);
  }

  async sendSupportEmail({ comment, user, userEmail, emailSentTo }: supportEmailTemplateProps): Promise<void> {
    const email = getSupportEmailTemplate({ comment, user, userEmail, emailSentTo });
    await this.eventService.sendSimpleEmail(email);
  }
}
